"use client";

import { useEffect, useRef } from "react";
import { useMenu } from "@/lib/menu/MenuProvider";
import { menus } from "@/lib/menu/menus";
import { openLink } from "@/lib/menu/Tooltip";
import {
  ChevronDownIcon,
  DiscordIcon,
  FileIcon,
  FlagIcon,
  HashIcon,
} from "./icons";
import { IconButton } from "./ui";
import { Pfp } from "./Pfp";

const DISCORD_URL = "https://discord.com";
const DOCS_URL = "https://docs.isolation.chat"; // placeholder — real docs host TBD

/* The strip above the card border (desktop) / atop the nav page (mobile).
   Its height is --card-t so it fills that exact strip. Left: brand mark.
   Center: current page. Right: docs, discord, divider, profile with a
   drop-down profile card. */
export function TopBar({ mobile = false }: { mobile?: boolean }) {
  const menu = useMenu();
  const { popup, isOpen } = menu;

  /* Ctrl K → search popup, Ctrl , → settings popup;
     f then 1 (typed in sequence) → dev panel — temporary debug hook,
     gets commented out before ship */
  const armed = useRef(0);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      /* the dev sequence skips editable fields — "f1" is typeable text */
      const ae = document.activeElement;
      const editable =
        ae instanceof HTMLElement &&
        ae !== document.body &&
        !!ae.closest("input, textarea, select, [contenteditable]");
      if (!editable) {
        const k = e.key.toLowerCase();
        if (k === "f") armed.current = Date.now();
        else if (k === "1" && Date.now() - armed.current < 600) {
          armed.current = 0;
          e.preventDefault();
          popup(menus.dev);
          return;
        }
      }
      if (!(e.ctrlKey || e.metaKey) || e.altKey || e.shiftKey) return;
      const k = e.key.toLowerCase();
      if (k === "k") {
        e.preventDefault();
        popup(menus.search);
      } else if (k === ",") {
        e.preventDefault();
        popup(menus.settings);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [popup]);

  const openDiscord = () =>
    openLink(menu, { text: "Discord", href: DISCORD_URL });
  const openDocs = () =>
    openLink(menu, { text: "Docs", href: DOCS_URL });

  return (
    <div
      className={`relative flex h-[var(--card-t)] shrink-0 items-center bg-chrome pl-3 ${
        mobile ? "pr-0" : "pr-3"
      }`}
    >
      {/* left — brand mark */}
      <span
        aria-hidden
        className="h-3.5 w-3.5 rounded-[4px] bg-cover bg-center"
        style={{ backgroundImage: "url(/favicon.webp)" }}
      />
      <span className="ml-1.5 text-meta font-semibold tracking-[0.14em] text-fg-mute uppercase">
        isolation
      </span>

      {/* center — current page (desktop only) */}
      {mobile ? null : (
        <span className="pointer-events-none absolute left-1/2 flex -translate-x-1/2 items-center gap-1.5">
          <HashIcon className="h-3.5 w-3.5 text-fg-faint" />
          <span className="text-note font-medium text-fg-mute">general</span>
        </span>
      )}

      {/* right — report, docs, discord, divider, profile */}
      <div className="ml-auto flex items-center gap-0.5">
        <IconButton
          label="Report"
          size="xs"
          tip={{ title: "Report", icon: FlagIcon }}
          onClick={() => popup(menus.report)}
        >
          <FlagIcon className="h-4 w-4" />
        </IconButton>
        <IconButton
          label="Docs"
          size="xs"
          tip={{ title: "Docs", icon: FileIcon }}
          onClick={openDocs}
        >
          <FileIcon className="h-4 w-4" />
        </IconButton>
        <IconButton
          label="Discord"
          size="xs"
          tip={{ title: "Discord", icon: DiscordIcon }}
          onClick={openDiscord}
        >
          <DiscordIcon className="h-4 w-4" />
        </IconButton>
        <span aria-hidden className="mx-1.5 h-3.5 w-px bg-line" />
        <button
          type="button"
          aria-haspopup="menu"
          aria-expanded={isOpen("profile-tr")}
          className="flex items-center gap-1.5 rounded-md px-1.5 py-1 transition-colors hover:bg-raise"
          /* the same account card as the dm bar's dropup, docked to the
             screen's top-right corner instead of over the profile bar —
             profileTr mirrors the flyout pullouts to grow left since the
             card hugs the screen's right edge. mobile: bottom card */
          onClick={() => popup(menus.profileTr, "tr")}
        >
          <Pfp
            size={18}
            rounded="rounded-md"
            surface="bg-chrome"
            src="/pfp.png"
          />
          <span className="text-note font-medium text-fg">Display Name</span>
          <ChevronDownIcon
            className={`h-3 w-3 text-fg-faint transition-[rotate] duration-150 ${
              isOpen("profile-tr") ? "rotate-180" : ""
            }`}
          />
        </button>
      </div>
    </div>
  );
}
